import { ref, unref, watch } from 'vue';
import QuickLRU from 'quick-lru';
// eslint-disable-next-line import/no-unresolved
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import * as logger from '../utils/logger';

const renderedPreviewCache = new QuickLRU({ maxSize: 100 });

/**
 * Asynchronously fetches a PDF corresponding to a given URL.
 * Throws an error if the PDF is invalid or could not be fetched.
 */
async function fetchPDF(pdfjs, url, fileOrigin) {
    try {
        return await pdfjs.getDocument(url).promise;
    } catch (exception) {
        logger.error({
            exception,
            fingerprint: ['Fetching PDF document failed'],
            extra: {
                fileUrl: url,
                fileOrigin,
            },
        });

        throw exception;
    }
}

/* eslint-disable no-param-reassign */
async function generateAndCachePreview(canvasElement, page, url, viewport) {
    const context = canvasElement.getContext('2d');

    canvasElement.width = Math.floor(viewport.width);
    canvasElement.height = Math.floor(viewport.height);
    canvasElement.style.width = `${Math.floor(viewport.width)}px`;
    canvasElement.style.height = `${Math.floor(viewport.height)}px`;

    const renderContext = {
        canvasContext: context,
        viewport,
    };

    await page.render(renderContext).promise;

    // After rendering the page, save it to the cache
    renderedPreviewCache.set(url, canvasElement.toDataURL('image/png'));
}
/* eslint-enable no-param-reassign */

async function loadCachedPreview(canvasElement, url) {
    const response = await fetch(renderedPreviewCache.get(url));
    const blob = await response.blob();
    const bitmap = await createImageBitmap(blob);
    canvasElement.getContext('2d').drawImage(bitmap, 0, 0);
}

async function setupPdfJS() {
    const pdfjs = await import(/* webpackChunkName: "pdfjs" */ 'pdfjs-dist/build/pdf.mjs');
    pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;
    return pdfjs;
}

async function getFirstPage(pdfjs, url, fileOrigin) {
    const pdf = await fetchPDF(pdfjs, url, fileOrigin);
    return pdf.getPage(1);
}

function getDesiredViewport(page) {
    const desiredPreviewWidth = 64;
    const originalViewport = page.getViewport({ scale: 1 });
    const scale = desiredPreviewWidth / originalViewport.width;
    return page.getViewport({ scale });
}

export const usePdfPreview = () => {
    const pdfPreviewAvailable = ref(false);
    const currentlyGeneratingPreview = ref(false);
    const renderQueue = ref([]);
    const renderOperationInProgress = ref(false);

    /**
     * Processes the next task in the queue if available.
     * Starts the next task only after the current task has been completed.
     */
    async function processRenderingQueue() {
        // if queue is empty or already processing a task then abort
        if (renderQueue.value.length === 0 || renderOperationInProgress.value) return;

        renderOperationInProgress.value = true;
        const task = renderQueue.value.shift();
        await task();
        renderOperationInProgress.value = false;
    }

    // process the renderQueue as either the queue or rendering changes
    watch([renderQueue, renderOperationInProgress], processRenderingQueue, { deep: true });

    async function renderCanvas({ canvas, fileUrl, fileOrigin, resolve, reject }) {
        currentlyGeneratingPreview.value = true;
        const url = new URL(fileUrl, window.location.origin).href;

        try {
            const pdfjs = await setupPdfJS();
            const page = await getFirstPage(pdfjs, url, fileOrigin);

            if (!page) {
                resolve();
                return;
            }

            const viewport = getDesiredViewport(page);
            const canvasElement = unref(canvas);

            if (!canvasElement?.getContext) {
                resolve();
                return;
            }

            await generateAndCachePreview(canvasElement, page, url, viewport);
            pdfPreviewAvailable.value = true;
            resolve();
        } catch (exception) {
            logger.error({
                exception,
                fingerprint: ['Generating PDF preview failed'],
                extra: { fileUrl: url, fileOrigin },
            });

            reject(exception);
        } finally {
            currentlyGeneratingPreview.value = false;
        }
    }

    /**
     * Generates a PDF preview from the given file URL and renders it on the given canvas.
     * If the rendered preview is already cached, it loads the cached preview or queues a rendering task otherwise.
     * */
    async function generatePdfPreview({ canvas, fileUrl, fileOrigin }) {
        const canvasRef = unref(canvas);

        let task;
        if (renderedPreviewCache.has(fileUrl)) {
            task = () => new Promise((resolve) => {
                loadCachedPreview(canvasRef, fileUrl);
                pdfPreviewAvailable.value = true;
                resolve();
            });
        } else {
            task = () => new Promise((resolve, reject) => {
                renderCanvas({ canvas, fileUrl, fileOrigin, resolve, reject })
                    .catch((exception) => {
                        logger.error({
                            exception,
                            fingerprint: ['Rendering PDF preview failed'],
                            extra: { fileUrl, fileOrigin },
                        });
                        reject(exception);
                    });
            });
        }
        renderQueue.value.push(task);
    }

    return { currentlyGeneratingPreview, pdfPreviewAvailable, generatePdfPreview };
};
